// Мастер «Новая учётная запись площадки». Панель, а не модалка: подключение записи обратимо —
// лишняя запись остаётся пустой и никому не мешает, необратимого в ней нет (правило 4).
//
// Запись заводится ПУСТОЙ и сама по себе не умеет ничего: ни вакансию разместить, ни отклик
// принять, ни переписку вести. Доступы кладутся вторым шагом, уже в карточке. Сказать это надо
// здесь, а не в карточке: человек, подключивший три записи подряд и не увидевший ни одного
// объявления, идёт разбираться, что сломалось, — хотя не сломалось ничего.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiPlugLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { newIdempotencyKey, useCreateAccountMutation } from '../queries';
import { MANAGE_OWNER, MANAGE_PERMISSION } from '../views/AccountsList';

export function NewAccountPanel({ onClose }: { onClose: () => void }) {
  const allowed = useCan(MANAGE_PERMISSION);

  // Ключ идемпотентности живёт столько же, сколько форма: двойное нажатие и повтор после
  // обрыва вернут ту же запись, а не заведут вторую с тем же названием.
  const [ключ] = useState(newIdempotencyKey);

  const [название, setНазвание] = useState('');
  const [примечание, setПримечание] = useState('');
  const [ошибкаНазвания, setОшибкаНазвания] = useState<string | undefined>(undefined);
  const [отказ, setОтказ] = useState<string | null>(null);

  const mutation = useCreateAccountMutation(ключ);

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(`Подключить запись не выйдет: права на это у вас нет. ${MANAGE_OWNER}`);
      return;
    }
    if (mutation.isPending) {
      setОтказ('Запись уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (название.trim() === '') {
      setОшибкаНазвания(
        'Введите название: записей у нас три, и различать их в списке человек будет по нему.',
      );
      return;
    }
    setОшибкаНазвания(undefined);
    setОтказ(null);
    mutation.mutate(
      {
        title: название.trim(),
        note: примечание.trim() === '' ? null : примечание.trim(),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && <NoPermissionNote что='подключать учётные записи площадки' кто={MANAGE_OWNER} />}

      <TextField
        id='new-account-title'
        label='Название'
        required
        value={название}
        onChange={setНазвание}
        placeholder='Например: основная запись'
        icon={RiPlugLine}
        maxLength={255}
        error={ошибкаНазвания}
        hint='Как называем эту запись между собой. На площадке это название не видно.'
      />

      <TextArea
        id='new-account-note'
        label='Примечание'
        value={примечание}
        onChange={setПримечание}
        rows={3}
        maxLength={2000}
        placeholder='Чья запись и для чего её держим'
        hint='Через полгода три записи выглядят одинаково; примечание отвечает, зачем нужна каждая.'
      />

      <Hint.Root>
        Запись заводится пустой: пока в ней нет доступов, она не умеет ни размещать вакансии, ни
        вести переписку. Доступы кладутся следующим шагом, в карточке записи, и их два — ключ
        компании для переписки и ключ сотрудника для вакансий.
      </Hint.Root>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Подключаем запись…' : 'Подключить запись'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
